import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Pagination,
  Card,
  Button,
  CardHeader,
  CardContent,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ImageCarouselDialog from 'app/views/Argyle/sites/ObservationSection/ImageCarouselDialog';
import { deleteARImage } from 'app/redux/reducers/ARImageReducer';
import { useDispatch } from 'react-redux';
import { useSite } from 'app/contexts/SiteContext';
import { PCIntegrationIcon } from 'app/components/AgyleComponents/ArgyleIcon/ProCoreIntegrationIcon';

import {
  formatTimestamp,
  sortAndGroupImages,
  flattenGroupedImages,
  groupImagesByDateKey,
} from 'app/utils';

const IMAGES_PER_PAGE = 6;
const FALLBACK_IMAGE = '/assets/images/argyle/newLogo/Argyle Logo - 01.svg';
const PROCORE_LOGO = '/assets/images/argyle/newLogo/PCOR.svg';

const ARImageGalleryPage = ({ images, projectId }) => {
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [manageMode, setManageMode] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const { siteInfo } = useSite();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const cols = isSmallScreen ? 1 : 3;

  const sortedGroupedImages = useMemo(
    () => sortAndGroupImages(images),
    [images],
  );

  const flatImages = useMemo(
    () => flattenGroupedImages(sortedGroupedImages),
    [sortedGroupedImages],
  );

  const totalPages = Math.ceil(flatImages.length / IMAGES_PER_PAGE);

  const paginatedImages = useMemo(() => {
    const start = (page - 1) * IMAGES_PER_PAGE;
    const end = page * IMAGES_PER_PAGE;
    return flatImages.slice(start, end);
  }, [flatImages, page]);

  const groupedPaginatedImages = useMemo(
    () => groupImagesByDateKey(paginatedImages),
    [paginatedImages],
  );

  const procoreProjectId = siteInfo?.procoreProjectId;
  const procoreImageCategoryId = siteInfo?.procoreImageCategoryId;
  const canSyncProcore = Boolean(procoreProjectId && procoreImageCategoryId);
  const ProcoreImageGalleryLink = `https://procore.com/${procoreProjectId}/project/images/show_album?id=${procoreImageCategoryId}`;

  const handleCloseCarousel = () => setCarouselOpen(false);
  const onImageClick = (imageId) => {
    if (!manageMode) {
      const index = imageIndexMap.get(imageId);

      setCarouselStartIndex(index != null ? index : 0); // fallback to 0
      setCarouselOpen(true);
    } else {
      setSelectedImageIds((prev) =>
        prev.includes(imageId)
          ? prev.filter((id) => id !== imageId)
          : [...prev, imageId],
      );
    }
  };

  const handleDownload = (e, image) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = image.augmentedUrl;
    link.download = image.augmentedUrl.split('/').pop();
    link.click();
  };

  const handleBatchDownload = async () => {
    for (const imageId of selectedImageIds) {
      const image = images.find((img) => img.id === imageId);
      if (image) {
        const link = document.createElement('a');
        link.href = image.augmentedUrl;
        link.download = image.augmentedUrl.split('/').pop();
        link.click();
        await new Promise((r) => setTimeout(r, 200));
      }
    }
  };

  const imageIndexMap = useMemo(() => {
    const map = new Map();
    // flatImages elements look like { image, localIndex, globalIndex }
    flatImages.forEach(({ image }, idx) => {
      map.set(image.id, idx); // ← use the String ID as the key
    });
    return map;
  }, [flatImages]);

  const handleBatchDelete = () => {
    selectedImageIds.forEach((imageId) =>
      dispatch(deleteARImage(projectId, imageId)),
    );
    setSelectedImageIds([]);
  };

  const handlePageChange = (_, value) => {
    setPage(value);
    setSelectedImageIds([]);
  };

  return (
    <Box p={3}>
      <Card sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center">
          <PhotoLibraryIcon fontSize="large" />
          <Typography variant="h2" ml={1}>
            AR Photo Gallery
          </Typography>
        </Box>
      </Card>

      <Card sx={{ p: 2, mb: 2 }}>
        {!manageMode ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setManageMode(true)}
          >
            Enter Manage Mode
          </Button>
        ) : (
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleBatchDownload}
              disabled={selectedImageIds.length === 0}
            >
              Download Selected
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleBatchDelete}
              disabled={selectedImageIds.length === 0}
              startIcon={<DeleteIcon />}
            >
              Delete Selected
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setManageMode(false);
                setSelectedImageIds([]);
              }}
            >
              Cancel Manage Mode
            </Button>
          </Box>
        )}
      </Card>

      {canSyncProcore && (
        <Card elevation={3} sx={{ mx: 'auto', mt: 4, mb: 4, borderRadius: 2 }}>
          <CardHeader
            title={<Typography variant="h6">Procore Image Gallery</Typography>}
            action={<PCIntegrationIcon />}
            sx={{ pb: 0 }}
          />
          <CardContent>
            <Button
              variant="contained"
              color="secondary"
              startIcon={
                <Box
                  component="img"
                  src={PROCORE_LOGO}
                  alt=""
                  sx={{ width: 20, height: 20 }}
                />
              }
              onClick={() => window.open(ProcoreImageGalleryLink, '_blank')}
            >
              View Album in Procore
            </Button>
          </CardContent>
        </Card>
      )}

      <Card sx={{ p: 2, mb: 2 }}>
        {groupedPaginatedImages.map((group, groupIndex) => (
          <Box key={groupIndex} mb={4}>
            <Typography variant="h6" gutterBottom>
              {group.date}
            </Typography>
            <ImageList cols={cols} gap={16}>
              {group.images.map(({ image, localIndex }) => {
                const imageId = image.id || `local-${localIndex}`;
                const isSelected = selectedImageIds.includes(imageId);
                const hasProcore = Boolean(image?.metadata?.procoreImageId);

                return (
                  <ImageListItem
                    key={imageId}
                    sx={{ cursor: 'pointer', position: 'relative' }}
                    onClick={() => onImageClick(imageId)}
                  >
                    <img
                      src={image.arThumbnailUrl}
                      alt={`Thumbnail for ${image.id}`}
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = FALLBACK_IMAGE;
                      }}
                      style={{
                        width: '100%',
                        display: 'block',
                        objectFit: 'contain',
                      }}
                    />
                    {hasProcore && (
                      <Box
                        position="absolute"
                        top={8}
                        right={8}
                        bgcolor="white"
                        p={0.5}
                        borderRadius={1}
                        zIndex={1}
                      >
                        <img
                          src={PROCORE_LOGO}
                          alt="Procore"
                          style={{ height: 24 }}
                        />
                      </Box>
                    )}
                    {manageMode && isSelected && (
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        bgcolor="rgba(0,0,0,0.5)"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 48, color: 'white' }}
                        />
                      </Box>
                    )}
                    <ImageListItemBar
                      title={
                        image?.metadata?.timeStamp
                          ? formatTimestamp(`${image.metadata.timeStamp}Z`)
                          : 'Image'
                      }
                      actionIcon={
                        !manageMode && (
                          <IconButton
                            onClick={(e) => handleDownload(e, image)}
                            sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        )
                      }
                    />
                  </ImageListItem>
                );
              })}
            </ImageList>
          </Box>
        ))}

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        <ImageCarouselDialog
          open={carouselOpen}
          onClose={handleCloseCarousel}
          images={flatImages}
          initialIndex={carouselStartIndex}
        />
      </Card>
    </Box>
  );
};

ARImageGalleryPage.propTypes = {
  images: PropTypes.array.isRequired,
  projectId: PropTypes.string.isRequired,
};

export default ARImageGalleryPage;
